import { ChevronLeft, ExternalLink, MapPinned } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card } from "../../../../components";
import PageHeader from "../../../../components/common/PageHeader";
import {
  neighborhoodFocusAreas,
  neighborhoodRecommendations,
} from "../data/neighborhoodsReport.data";

const reachTone = (reach: number) =>
  reach >= 25
    ? "bg-[#008a72] text-white"
    : reach >= 22
      ? "bg-[#94dfcd] text-[#005442]"
      : "bg-[#ffc45c] text-[#805200]";

export default function NeighborhoodReachReportPage() {
  const navigate = useNavigate();
  const [selectedCode, setSelectedCode] = useState("D");
  const selectedArea = useMemo(
    () =>
      neighborhoodFocusAreas.find((area) => area.code === selectedCode) ??
      neighborhoodFocusAreas[0],
    [selectedCode],
  );

  return (
    <main className="min-h-full bg-[#effbf3] px-4 py-6 text-[#004b3a] sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1440px] space-y-6">
        <PageHeader
          title="Where is engagement strong, and who is underserved?"
          subtitle={
            <span>
              <span className="font-bold uppercase tracking-[0.12em] text-[#008a72]">
                Neighborhoods
              </span>
              <br />
              Eight NRI focus areas, adjusted for population
            </span>
          }
          actions={
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate("/reports")}
              >
                <ChevronLeft />
                Reports
              </Button>
              <Button
                type="button"
                variant="brand-outline"
                onClick={() => window.print()}
              >
                <ExternalLink />
                Export report
              </Button>
            </div>
          }
        />
        <section className="grid gap-6 xl:grid-cols-[minmax(0,1.15fr)_minmax(380px,.85fr)]">
          <Card className="rounded-3xl border border-[#dbe9e1] bg-white p-5 shadow-sm sm:p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-3">
                  <MapPinned className="h-5 w-5 text-[#008a72]" />
                  <h2 className="text-xl font-bold">Focus area map</h2>
                </div>
                <p className="mt-2 text-sm text-[#587268]">
                  Select an area to see its participation profile and outreach opportunities.
                </p>
              </div>
              <span className="rounded-full bg-[#edf8f1] px-3 py-1 text-xs font-bold">
                Engaged per 1,000
              </span>
            </div>
            <div className="mx-auto mt-8 grid max-w-2xl grid-cols-2 gap-3 sm:grid-cols-4">
              {neighborhoodFocusAreas.map((area) => (
                <button
                  key={area.code}
                  type="button"
                  onClick={() => setSelectedCode(area.code)}
                  className={`min-h-28 rounded-2xl p-3 text-center shadow-sm transition hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-[#006f5a] ${reachTone(area.reach)} ${area.code === selectedArea.code ? "ring-4 ring-[#005442] ring-offset-2" : ""}`}
                >
                  <span className="block text-sm font-extrabold leading-tight">{area.name}</span>
                  <span className="mt-2 block text-xl font-extrabold">{area.reach}</span>
                </button>
              ))}
            </div>
            <div className="mt-7 flex flex-wrap gap-3 text-xs font-semibold text-[#587268]">
              <span className="flex items-center gap-1">
                <i className="h-3 w-3 rounded bg-[#ffc45c]" />
                Under 22
              </span>
              <span className="flex items-center gap-1">
                <i className="h-3 w-3 rounded bg-[#94dfcd]" />
                22–24
              </span>
              <span className="flex items-center gap-1">
                <i className="h-3 w-3 rounded bg-[#008a72]" />
                25 and up
              </span>
            </div>
          </Card>
          <Card className="rounded-3xl border border-[#dbe9e1] bg-white p-5 shadow-sm sm:p-6">
            <div className="flex items-start gap-4">
              <span className="grid h-14 w-14 place-items-center rounded-2xl bg-[#00c49a] text-2xl font-extrabold">
                {selectedArea.code}
              </span>
              <div>
                <h2 className="text-2xl font-bold">{selectedArea.name}</h2>
                <p className="mt-1 text-sm text-[#587268]">
                  NRI focus area · population-adjusted participation view
                </p>
              </div>
            </div>
            <section className="mt-5 rounded-3xl bg-[#004b3a] p-5 text-white">
              <p className="text-xs font-bold uppercase tracking-[0.1em] text-[#f9c75c]">
                Participation outlook
              </p>
              <div className="mt-4 grid grid-cols-3 gap-3">
                <div>
                  <strong className="block text-2xl">{selectedArea.engagement}</strong>
                  <span className="text-xs text-[#d8efe5]">engagement</span>
                </div>
                <div>
                  <strong className="block text-2xl">{selectedArea.users}</strong>
                  <span className="text-xs text-[#d8efe5]">CivicPoints users</span>
                </div>
                <div>
                  <strong className="block text-2xl">{selectedArea.growth}</strong>
                  <span className="text-xs text-[#d8efe5]">growth</span>
                </div>
              </div>
            </section>
            <div className="mt-5 grid grid-cols-2 gap-3">
              {[
                ["Population", selectedArea.population],
                ["Adoption rate", selectedArea.adoption],
                ["Engaged per 1,000", String(selectedArea.reach)],
                ["Priority", selectedArea.reach < 22 ? "High" : "Watch"],
              ].map(([label, value]) => (
                <article
                  className="rounded-2xl bg-[#edf8f1] p-4"
                  key={label}
                >
                  <p className="text-xs font-bold text-[#587268]">{label}</p>
                  <strong className="mt-2 block text-2xl">{value}</strong>
                </article>
              ))}
            </div>
            <aside className="mt-5 rounded-2xl bg-[#fff0cf] p-4 text-sm font-bold text-[#805200]">
              Target outreach where distance, awareness, and program availability suppress
              participation.
            </aside>
            <Button
              className="mt-5 w-full"
              type="button"
              variant="brand-secondary"
              onClick={() => navigate("/reports/residents")}
            >
              View residents
            </Button>
          </Card>
        </section>
        <section className="grid gap-6 xl:grid-cols-2">
          <Card className="rounded-3xl border border-[#dbe9e1] bg-white p-5 shadow-sm sm:p-6">
            <h2 className="text-xl font-bold">Participation gaps &amp; opportunities</h2>
            <p className="mt-2 text-sm text-[#587268]">
              Targeted outreach metrics for each focus area.
            </p>
            <div className="mt-5 overflow-x-auto">
              <table className="w-full min-w-[660px] text-left text-sm">
                <thead className="border-b text-xs uppercase text-[#587268]">
                  <tr>
                    <th className="pb-3">Area name</th>
                    <th className="pb-3">Population</th>
                    <th className="pb-3">Users</th>
                    <th className="pb-3">Adoption</th>
                    <th className="pb-3">Engagement</th>
                    <th className="pb-3">Growth</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#edf3ef]">
                  {neighborhoodFocusAreas.map((area) => (
                    <tr key={area.code}>
                      <td className="py-4 font-bold">{area.name}</td>
                      <td className="py-4">{area.population}</td>
                      <td className="py-4">{area.users}</td>
                      <td className="py-4">{area.adoption}</td>
                      <td className="py-4">{area.engagement}</td>
                      <td className="py-4">{area.growth}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
          <Card className="rounded-3xl border border-[#dbe9e1] bg-white p-5 shadow-sm sm:p-6">
            <h2 className="text-xl font-bold">Lowest reach, and what might help</h2>
            <p className="mt-2 text-sm text-[#587268]">
              Recommended interventions by reported barrier.
            </p>
            <div className="mt-5 divide-y divide-[#edf3ef]">
              {neighborhoodRecommendations.map((item) => (
                <article
                  className="flex gap-3 py-4"
                  key={item.code}
                >
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#fff0cf] font-extrabold text-[#805200]">
                    {item.code}
                  </span>
                  <div>
                    <p className="font-bold">
                      {item.area}{" "}
                      <span className="font-medium text-[#587268]">· {item.reach} per 1,000</span>
                    </p>
                    <p className="mt-1 text-sm text-[#587268]">Barrier: {item.barrier}</p>
                    <p className="mt-1 text-sm font-bold text-[#006f5a]">Try: {item.action}</p>
                  </div>
                </article>
              ))}
            </div>
          </Card>
        </section>
      </div>
    </main>
  );
}
